import React, { useRef } from 'react';
import type {
  DatesSetArg,
  DayCellContentArg,
  EventClickArg,
  EventContentArg,
  EventInput,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';

import { DAY_GRID_MONTH } from '../constants';
import { getDomainFamily } from '@/common/domains';
import { ADAPTIVE } from '@/constants';
import { getLayoutContentHeight } from '@/utils/browser';

interface CalendarViewProps {
  initialView: string;
  today: string;
  events: EventInput[];
  onDateChange: (dateInfo: DatesSetArg) => void;
  renderDayCell: (dayRenderInfo: DayCellContentArg) => React.ReactNode;
  renderEventContent: (eventInfo: EventContentArg) => React.ReactNode;
}

export const CalendarView = ({
  initialView,
  today,
  events,
  onDateChange,
  renderDayCell,
  renderEventContent,
}: CalendarViewProps) => {
  const calendarRef = useRef(null);

  const handleEventClick = (info: EventClickArg) => {
    let experimentName = info.event.title;

    const isAdaptive = info.event.extendedProps.type === ADAPTIVE;
    if (isAdaptive) {
      experimentName = info?.event?.title?.split('.')?.[1] ?? '';
    }

    if (experimentName)
      window.open(
        `/${getDomainFamily()}/experiments/${experimentName}/overview`,
      );
  };

  return (
    <FullCalendar
      key={initialView}
      ref={calendarRef}
      initialDate={today}
      initialView={initialView}
      contentHeight={getLayoutContentHeight() - 180}
      dayHeaderFormat={{ weekday: 'long' }}
      dayCellContent={renderDayCell}
      plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
      headerToolbar={{
        start: 'today,prev,next,title',
        center: '',
        end: '',
      }}
      titleFormat={{ year: 'numeric', month: 'long' }}
      events={events}
      dayMaxEvents={initialView === DAY_GRID_MONTH ? 5 : Infinity}
      eventContent={renderEventContent}
      datesSet={onDateChange}
      eventClick={handleEventClick}
      eventDisplay="block"
    />
  );
};
